import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { siteAPI, referralAPI, faqAPI } from '../lib/api';
import { 
  Menu, X, ChevronRight, ChevronDown, Star, TrendingUp, BarChart3, 
  MessageSquare, FileText, Check, Globe, Zap, Shield,
  MapPin, Clock, Phone, Users
} from 'lucide-react';
import PageMeta from '../components/PageMeta';

const LandingPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [faqs, setFaqs] = useState([]);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [siteMeta, setSiteMeta] = useState({});

  useEffect(() => {
    // Track referral link
    const refCode = searchParams.get('ref');
    if (refCode) {
      localStorage.setItem('ref_code', refCode);
      localStorage.setItem('ref_timestamp', Date.now().toString());
      // Track click (fire and forget)
      referralAPI.trackClick(refCode).catch(() => {});
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await siteAPI.getStatus();
        // Check if admin is logged in (either via main auth or admin auth)
        const isAdmin = user?.is_admin || (() => {
          try {
            const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
            return adminUser?.is_admin === true;
          } catch { return false; }
        })();
        if (res.data.maintenance_enabled && !isAdmin) {
          navigate('/maintenance');
          return;
        }
        if (res.data.alert_enabled && res.data.alert_message) {
          setAlertMessage(res.data.alert_message);
        }
        if (res.data.meta_title || res.data.meta_description) {
          setSiteMeta({ title: res.data.meta_title, description: res.data.meta_description });
        }
      } catch { /* ignore */ }
    };
    fetchStatus();
  }, [navigate, user]);

  useEffect(() => {
    faqAPI.getAll().then(res => setFaqs(res.data.faqs || [])).catch(() => {});
  }, []);

  const features = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'AI Post Generator',
      description: 'Create engaging Google Business posts in seconds with AI that understands your business.'
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: 'Review Reply Generator',
      description: 'Generate professional, personalized responses to customer reviews automatically.'
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: 'Profile Optimization',
      description: 'Get an instant audit score with actionable recommendations to improve visibility.'
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Performance Analytics',
      description: 'Track which posts get the most views and optimize your content strategy.'
    }
  ];

  const stats = [
    { value: '50+', label: 'Businesses Managed' },
    { value: 'Growing', label: 'Client Network' },
    { value: 'AI-Powered', label: 'Content & Review Tools' },
    { value: 'Focused on', label: 'Better Local Visibility' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <PageMeta
        title={siteMeta.title || 'GMB Autopilot - AI-Powered Google Business Profile Tools'}
        description={siteMeta.description || 'Optimize your Google Business Profile with AI-powered tools. Auto-posting, review management, analytics, and more.'}
      />
      {/* Alert Banner */}
      {alertMessage && (
        <div className="bg-[#FEF7E0] border-b border-[#F9AB00] px-4 py-2 overflow-hidden" data-testid="alert-banner">
          <div className="animate-marquee whitespace-nowrap">
            <span className="text-sm font-medium text-[#202124] inline-block">{alertMessage}</span>
          </div>
        </div>
      )}
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <div className="w-8 h-8 bg-[#1A73E8] rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
                GMB Autopilot
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-[#5F6368] hover:text-[#202124] font-medium transition-colors" data-testid="nav-features">
                Features
              </a>
              <a href="#how-it-works" className="text-[#5F6368] hover:text-[#202124] font-medium transition-colors" data-testid="nav-how">
                How It Works
              </a>
              <Link to="/pricing" className="text-[#5F6368] hover:text-[#202124] font-medium transition-colors" data-testid="nav-pricing">
                Pricing
              </Link>
              <Link to="/leaderboard" className="text-[#5F6368] hover:text-[#202124] font-medium transition-colors" data-testid="nav-leaderboard">
                Leaderboard
              </Link>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-4">
              {user ? (
                <Link to="/dashboard" className="btn-google" data-testid="go-to-dashboard">
                  Go to Dashboard
                </Link>
              ) : (
                <>
                  <Link to="/login" className="text-[#5F6368] hover:text-[#202124] font-medium" data-testid="nav-login">
                    Sign In
                  </Link>
                  <Link to="/register" className="btn-google" data-testid="nav-register">
                    Get Started Free
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-[#DADCE0] py-4 px-6">
            <nav className="flex flex-col gap-4">
              <a href="#features" className="text-[#5F6368] hover:text-[#202124] font-medium">Features</a>
              <a href="#how-it-works" className="text-[#5F6368] hover:text-[#202124] font-medium">How It Works</a>
              <Link to="/pricing" className="text-[#5F6368] hover:text-[#202124] font-medium">Pricing</Link>
              <hr className="border-[#DADCE0]" />
              {user ? (
                <Link to="/dashboard" className="btn-google text-center">Go to Dashboard</Link>
              ) : (
                <>
                  <Link to="/login" className="btn-outline text-center">Sign In</Link>
                  <Link to="/register" className="btn-google text-center">Get Started Free</Link>
                </>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="py-20 lg:py-32 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 bg-[#E8F0FE] text-[#1A73E8] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Zap className="w-4 h-4" />
              AI-Powered Google Business Optimization
            </div>
            <h1 className="text-5xl lg:text-6xl tracking-tighter font-semibold text-[#202124] leading-tight" style={{ fontFamily: 'Outfit' }}>
              Grow Your Local Business with
              <span className="text-[#1A73E8]"> Google</span>
            </h1>
            <p className="mt-6 text-lg text-[#5F6368] leading-relaxed max-w-xl">
              Connect your Google Business Profile and let AI create engaging posts, 
              reply to reviews, and optimize your profile for maximum visibility.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link 
                to={user ? "/dashboard" : "/register"} 
                className="btn-google inline-flex items-center justify-center gap-2"
                data-testid="hero-cta"
              >
                Connect Your GMB Profile
                <ChevronRight className="w-5 h-5" />
              </Link>
              <a href="#how-it-works" className="btn-outline inline-flex items-center justify-center">
                See How It Works
              </a>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-[#5F6368]">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#1E8E3E]" />
                Free forever plan
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#1E8E3E]" />
                No credit card required
              </div>
            </div>
          </div>

          {/* Hero Image - GMB Preview */}
          <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <div className="bg-white rounded-2xl shadow-2xl border border-[#DADCE0] overflow-hidden">
              {/* Mock GMB Card */}
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <img 
                    src="https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=80&h=80&fit=crop" 
                    alt="Business" 
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#202124] text-lg">Sunrise Coffee House</h3>
                    <p className="text-sm text-[#5F6368]">Coffee Shop · Mumbai</p>
                    <div className="flex items-center gap-1 mt-1">
                      <Star className="w-4 h-4 fill-[#F9AB00] text-[#F9AB00]" />
                      <span className="font-medium text-[#202124]">4.6</span>
                      <span className="text-[#5F6368]">(287 reviews)</span>
                    </div>
                  </div>
                  <div className="px-3 py-1 bg-[#E6F4EA] text-[#1E8E3E] text-sm font-medium rounded-full">
                    Verified
                  </div>
                </div>
                
                {/* Quick Info */}
                <div className="mt-6 grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 bg-[#F8F9FA] rounded-lg">
                    <Phone className="w-5 h-5 mx-auto text-[#1A73E8]" />
                    <p className="text-xs text-[#5F6368] mt-1">Call</p>
                  </div>
                  <div className="p-3 bg-[#F8F9FA] rounded-lg">
                    <MapPin className="w-5 h-5 mx-auto text-[#1A73E8]" />
                    <p className="text-xs text-[#5F6368] mt-1">Directions</p>
                  </div>
                  <div className="p-3 bg-[#F8F9FA] rounded-lg">
                    <Globe className="w-5 h-5 mx-auto text-[#1A73E8]" />
                    <p className="text-xs text-[#5F6368] mt-1">Website</p>
                  </div>
                </div>

                {/* Audit Score */}
                <div className="mt-6 p-4 bg-gradient-to-r from-[#E8F0FE] to-[#F8F9FA] rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#5F6368]">Profile Optimization Score</p>
                      <p className="text-2xl font-bold text-[#1A73E8]">78/100</p>
                    </div>
                    <div className="w-16 h-16 rounded-full border-4 border-[#1A73E8] flex items-center justify-center bg-white">
                      <TrendingUp className="w-6 h-6 text-[#1A73E8]" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#F8F9FA] border-y border-[#DADCE0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl lg:text-4xl font-bold text-[#1A73E8]" style={{ fontFamily: 'Outfit' }}>
                  {stat.value}
                </p>
                <p className="text-[#5F6368] mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#5F6368] mb-4">
              POWERFUL FEATURES
            </p>
            <h2 className="text-3xl lg:text-4xl tracking-tight font-medium text-[#202124]" style={{ fontFamily: 'Outfit' }}>
              Everything You Need to Dominate Local Search
            </h2>
            <p className="mt-4 text-[#5F6368]">
              Our AI-powered tools help you create content, manage reviews, and optimize your profile.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <div 
                key={i} 
                className="dashboard-card group cursor-pointer"
                data-testid={`feature-card-${i}`}
              >
                <div className="w-12 h-12 bg-[#E8F0FE] rounded-lg flex items-center justify-center text-[#1A73E8] group-hover:bg-[#1A73E8] group-hover:text-white transition-colors">
                  {feature.icon}
                </div>
                <h3 className="mt-4 text-lg font-semibold text-[#202124]">{feature.title}</h3>
                <p className="mt-2 text-sm text-[#5F6368]">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 px-6 lg:px-8 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#5F6368] mb-4">
              HOW IT WORKS
            </p>
            <h2 className="text-3xl lg:text-4xl tracking-tight font-medium text-[#202124]" style={{ fontFamily: 'Outfit' }}>
              Get Started in 3 Simple Steps
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Connect Your GMB', desc: 'Link your Google Business Profile securely with one click.' },
              { step: '02', title: 'AI Analyzes Your Profile', desc: 'Get instant optimization score and personalized recommendations.' },
              { step: '03', title: 'Publish & Grow', desc: 'Create AI posts, reply to reviews, and watch your visibility grow.' }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 mx-auto bg-[#1A73E8] text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-[#202124]">{item.title}</h3>
                <p className="mt-2 text-[#5F6368]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <section className="py-20 px-6 lg:px-8 bg-[#F8F9FA]" id="faqs" data-testid="faq-section">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl tracking-tight font-bold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
                Frequently Asked{' '}
                <span className="text-[#1A73E8]">Questions</span>
              </h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={faq.id || index}
                  className="bg-white rounded-xl border border-[#DADCE0] overflow-hidden transition-shadow hover:shadow-sm"
                  data-testid={`faq-accordion-${index}`}
                >
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full flex items-center justify-between px-6 py-5 text-left"
                  >
                    <span className="font-medium text-[#202124] pr-4">{faq.question}</span>
                    <div className={`shrink-0 w-8 h-8 rounded-full bg-[#F1F3F4] flex items-center justify-center transition-transform ${openFaqIndex === index ? 'rotate-180' : ''}`}>
                      <ChevronDown className="w-4 h-4 text-[#5F6368]" />
                    </div>
                  </button>
                  {openFaqIndex === index && (
                    <div className="px-6 pb-5 text-[#5F6368] leading-relaxed border-t border-[#F1F3F4] pt-4">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-8 bg-[#1A73E8]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl tracking-tight font-medium text-white" style={{ fontFamily: 'Outfit' }}>
            Ready to Grow Your Local Business?
          </h2>
          <p className="mt-4 text-white/80 text-lg">
            Join thousands of businesses using GMB Autopilot to attract more customers.
          </p>
          <Link 
            to={user ? "/dashboard" : "/register"} 
            className="mt-8 inline-flex items-center gap-2 bg-white text-[#1A73E8] font-semibold px-8 py-4 rounded-lg hover:bg-[#F8F9FA] transition-colors"
            data-testid="cta-bottom"
          >
            Get Started Free
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-8 bg-[#202124] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#1A73E8] rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-semibold" style={{ fontFamily: 'Outfit' }}>
                GMB Autopilot
              </span>
            </div>
            <div className="flex gap-8 text-sm text-[#9AA0A6]">
              <Link to="/pricing" className="hover:text-white">Pricing</Link>
              <a href="#features" className="hover:text-white">Features</a>
              <Link to="/leaderboard" className="hover:text-white">Leaderboard</Link>
              <Link to="/login" className="hover:text-white">Sign In</Link>
            </div>
          </div>
          <div className="flex justify-center gap-6 mt-6 text-sm text-[#9AA0A6]">
            <Link to="/page/terms" className="hover:text-white">Terms & Conditions</Link>
            <Link to="/page/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/page/about" className="hover:text-white">About Us</Link>
          </div>
          <div className="mt-8 pt-8 border-t border-[#5F6368] text-center text-sm text-[#9AA0A6]">
            © 2025 GMB Autopilot. Not affiliated with Google. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
